import React from "react";
import Icon from "assets/icons";
import classNames from "classnames";
import { PriceDetails } from "./components";
import Button from "shared/components/button";
import useTicketFooterService from "./service";
import { TicketCardFlowType } from "../../enum";
import { TicketFooterPropTypes } from "./types";
import { useCopies } from "shared/hooks/useCopies";
import { getFormattedCurrency } from "shared/utils";
import ToolTip from "shared/components/tooltip";
import Size from "shared/enums/Size";

function TicketFooter(props: TicketFooterPropTypes) {
  const copies = useCopies();
  const service = useTicketFooterService(props);

  return (
    /**
     * 1. `showSingleBuyButtonAtOnce` will be passed when tickets page is displayed via bulk coupon code URL or via sponsored tickets URL
     * 2. We don't show price upfront when tickets page is displayed via bulk coupon code URL and adjust spacing accordingly using `singleBuyButtonAtOnce` css class
     */
    <div
      className={classNames("ticketCard_footer", {
        singleBuyButtonAtOnce: props.showSingleBuyButtonAtOnce && service.shouldShowBulkTicketButton
      })}>
      {service.shouldShowPriceDetails && (
        <div className="ticketCard_footerPriceWrapper">
          <div className="ticketCard_footerPriceWrapper--price">
            {/* Show currentPrice & priceDifference for Upgrade Ticket in MyExp */}
            {props.ticketCardFlowType === TicketCardFlowType.UPGRADE_TICKET && (
              <React.Fragment>
                {props.priceDifference && (
                  <strong className="ticketCard_footerPriceWrapper--priceToPay">
                    {getFormattedCurrency(props.priceDifference, service.localeId)}
                  </strong>
                )}
                {props.currentPrice && service.hasPriceDifference && (
                  <div className="ticketCard_footerPriceWrapper--priceAmount">
                    {getFormattedCurrency(props.currentPrice, service.localeId)}
                  </div>
                )}
              </React.Fragment>
            )}
            {/* Show price details in Get Ticket in MyExp & Reg */}
            {service.shouldShowPriceDetailsInGetTicket && (
              <PriceDetails
                localeId={props.localeId}
                applicableRate={props.applicableRate}
                ticketDisplayColour={props.ticketDisplayColour}
              />
            )}
          </div>
        </div>
      )}
      <div
        className="ticketCard_footerAction"
        style={
          props.ticketDisplayColour
            ? ({
                "--btnPrimaryOutlineColor": props.ticketDisplayColour,
                "--btnPrimaryOutlineTextColor": props.ticketDisplayColour
              } as React.CSSProperties)
            : {}
        }>
        {/* purchase button in ticket list & bulk ticket modal on reg */}
        {service.shouldShowTicketButton && props.ticketUrl ? (
          <Button
            isOutline
            iconPositionRight
            icon={<Icon.ArrowRightLong />}
            label={service.buyTicketButtonLabel}
            onClick={() => service.getTicketClickHandler(true)}
          />
        ) : (
          //  purchase button in ticket list in MyExp
          !props.ticketUrl &&
          props.buyTicketClickHandler && (
            <Button
              isOutline
              iconPositionRight
              icon={<Icon.ArrowRightLong />}
              label={service.buyTicketButtonLabel}
              onClick={() => service.getTicketClickHandler()}
            />
          )
        )}
        {/* bulk ticket purchase button in tickets list on reg */}
        {service.shouldShowBulkTicketButton && (
          <Button
            isOutline
            iconPositionRight
            icon={<Icon.ArrowRightLong />}
            label={copies.get("bulk.ticket.buy.in.bulk")}
            onClick={() => service.getTicketClickHandler()}
          />
        )}
      </div>
      <div className="ticketCard_footerTermCondition">
        {props.termsAndConditions && (
          <ToolTip
            trigger={"click"}
            size={Size.SMALL}
            content={props.termsAndConditions}
            isHTMLFormatted
            children={<span>{copies.get("terms.and.conditions")}</span>}
          />
        )}
      </div>
    </div>
  );
}

export default TicketFooter;
